import { genericApiError, isAxiosResponseOk } from '../../../api/api-common';
import apiManager from '../../../api/api-manager';
import { PersonaTipoEnum } from '../../../api/generated';
import {
  DocumentBeanComplexMetadata,
  DocumentSearchFilterRequest,
} from '../../../api/generated/papyrus-metadata';
import {
  formatDateFromForServer,
  formatDateToForServer,
} from '../../../api/parsers/commons-api-parsers';
import {
  filterArchivedDocuments,
  filterDocumentsBundle,
  parseCommunicationDocumentItem,
  parseContractDocumentItem,
  parseDispositionArchived,
  parseDispositionBundles,
  parseExpiredDocumentArchived,
  parseExpiredDocumentsBundles,
  parseOfferingDocumentItem,
} from '../../../api/parsers/documents-api-parsers';
import {
  communicationBenefitsTypes,
  communicationDividendsTypes,
  communicationOperationsTypes,
  communicationOtherLettersTypes,
  CommunicationsDocument,
  CommunicationsFilter,
  ContractDocument,
  DispositionDocument,
  ExpiredDocument,
  OfferingDocumentGroup,
  Practice,
} from '../../../model';
import {
  UnreadDocumentsCount,
  UnreadDocumentsCountsMap,
} from '../../../model/documents/unread-documents-count';
import { DownloadDocumentsError } from '../../../store';
import { AsyncThunkConfig } from '../../../store/async-thunk-config';
import {
  addFilter,
  addOfferFilter,
  countDocumentsByFilter,
  getAPIRequestHeadersWithToken,
  getBundlesDetails,
  GetThunkAPI,
  searchDocuments,
  searchDocumentsByFilter,
} from '../../../utils/api-utils';
import { DocumentsRepository } from '../documents-repository';
import {
  contractsDocumentFilter,
  dispositionsDocumentFilter,
  expiredDocumentsFilter,
} from './document-repository-papyrus-filters';
import {
  unreadCountFiltersList,
  UnreadCountItemIndex,
} from './document-repository-unread-count-list';

const shouldFetchAccessoryDocuments = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>,
  filter: Omit<DocumentSearchFilterRequest, 'paginator'>
): boolean => {
  const userState = thunkApi.getState().data.user;
  return (
    userState.fiduciaria &&
    !!userState.codiceRubrica &&
    !!filter.customerUserRegisterCode
  );
};

const getAccessoryDocumentsFilter = (
  filter: Omit<DocumentSearchFilterRequest, 'paginator'>
): Omit<DocumentSearchFilterRequest, 'paginator'> => ({
  ...filter,
  customerUserRegisterCode: undefined,
});

const expiredSkippedDocumentTypes: string[] = [
  'ADESIONE_SERVIZI_DIGITALI',
  'CAMBIO_RECAPITO_SERVIZI_DIGITALI',
];

const shouldSkipDocumentType = (
  documentType: string | undefined,
  skippedDocumentTypes: string[] = []
): boolean => !!documentType && skippedDocumentTypes.includes(documentType);

const mergeDocumentsIntoMap = (
  resultMap: Map<string, DocumentBeanComplexMetadata>,
  documents: Array<DocumentBeanComplexMetadata>,
  skippedDocumentTypes: string[] = []
): void => {
  documents.forEach((doc) => {
    if (
      doc.id &&
      !shouldSkipDocumentType(doc.documentType, skippedDocumentTypes)
    ) {
      resultMap.set(doc.id, doc);
    }
  });
};

const fetchAndMergeAccessoryDocuments = async (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>,
  filter: Omit<DocumentSearchFilterRequest, 'paginator'>,
  resultMap: Map<string, DocumentBeanComplexMetadata>,
  token?: string,
  skippedDocumentTypes: string[] = []
): Promise<void> => {
  if (!shouldFetchAccessoryDocuments(thunkApi, filter)) {
    return;
  }

  const accessoryResults: Array<DocumentBeanComplexMetadata> =
    await searchDocumentsByFilter(
      thunkApi,
      getAccessoryDocumentsFilter(filter),
      token
    );

  mergeDocumentsIntoMap(resultMap, accessoryResults, skippedDocumentTypes);
};

const countUnreadDocumentsInPractice = (practice: Practice<any>[]): number => {
  const uniqueIds = new Set<string>();
  return (practice || [])
    .flatMap((item) => item.documents)
    .flatMap((item) => item)
    .filter((item) => item.flagIsRead === false)
    .filter((item) => {
      if (!item.id) return true;
      if (uniqueIds.has(item.id)) return false;
      uniqueIds.add(item.id);
      return true;
    }).length;
};

const countDocumentsSection = async (
  repo: PapyrusDocumentsRepository,
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
): Promise<UnreadDocumentsCountsMap> => {
  const resultsList = await Promise.allSettled(
    ['getContractsList', 'getDispositionsList', 'getExpiredDocumentsList'].map(
      (item) => repo[item].call(this, thunkApi)
    )
  );

  return resultsList.reduce(
    (prev, curr, index) => {
      if (curr.status === 'fulfilled') {
        if (index === 0) {
          return {
            ...prev,
            Contract: curr.value?.filter((item) => item.flagIsRead === false)
              ?.length,
          };
        } else if (index === 1) {
          return {
            ...prev,
            Disposition: countUnreadDocumentsInPractice(curr.value),
          };
        } else {
          return {
            ...prev,
            ExpiredDocuments: countUnreadDocumentsInPractice(curr.value),
          };
        }
      }

      return prev;
    },
    {
      Contract: 0,
      Disposition: 0,
      ExpiredDocuments: 0,
    } as UnreadDocumentsCountsMap
  );
};

const getMappingIdCesamOw = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
): { idProspect: string; idActual: string } => {
  const user = thunkApi.getState().data.user;
  return {
    idProspect: user.prospectCode,
    idActual: user.userCode,
  };
};

const getMappingIdCesamOwRappresentante = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
): { idProspect: string; idActual: string } => {
  const user = thunkApi.getState().data.user;
  return {
    idProspect: user.codiceProspectRappresentante,
    idActual: user.userBehalfOfCode,
  };
};

const isNecessaryProspectCall = (
  thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
): boolean => {
  const state = thunkApi.getState();
  const user = state.data?.user;
  const isPg = user.userType === PersonaTipoEnum.Pg;
  const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
  const mappingIdCesamOwRappresentante =
    getMappingIdCesamOwRappresentante(thunkApi);

  return isPg
    ? mappingIdCesamOwRappresentante.idProspect &&
        mappingIdCesamOwRappresentante.idProspect !==
          mappingIdCesamOwRappresentante.idActual
    : mappingIdCesamOw.idProspect &&
        mappingIdCesamOw.idProspect !== mappingIdCesamOw.idActual;
};

class PapyrusDocumentsRepository implements DocumentsRepository {
  getContractsList = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ): Promise<ContractDocument[]> => {
    const state = thunkApi.getState();
    const user = state.data?.user;
    const isPg = user.userType === PersonaTipoEnum.Pg;
    const resultMap = new Map<string, DocumentBeanComplexMetadata>();
    const currentFilter = addFilter(thunkApi, { ...contractsDocumentFilter });
    const currentResults: Array<DocumentBeanComplexMetadata> =
      await searchDocumentsByFilter(thunkApi, {
        ...currentFilter,
      });

    mergeDocumentsIntoMap(resultMap, currentResults);

    await fetchAndMergeAccessoryDocuments(thunkApi, currentFilter, resultMap);

    const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
    const mappingIdCesamOwRappresentante =
      getMappingIdCesamOwRappresentante(thunkApi);

    if (isNecessaryProspectCall(thunkApi)) {
      const filter = {
        ...addFilter(thunkApi, { ...contractsDocumentFilter }),
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect: Array<DocumentBeanComplexMetadata> =
        await searchDocumentsByFilter(thunkApi, filter, prospectToken);

      mergeDocumentsIntoMap(resultMap, resultProspect);
    }

    return (
      Array.from(resultMap.values())
        //.filter((item) => item.status === 'SIGNED')
        .map((item) => parseContractDocumentItem(item))
    );
  };

  getDispositionsList = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ): Promise<Practice<DispositionDocument>[]> => {
    const state = thunkApi.getState();
    const user = state.data?.user;
    const isPg = user.userType === PersonaTipoEnum.Pg;

    let result = await searchDocuments(thunkApi, {
      ...dispositionsDocumentFilter,
    });

    const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
    const mappingIdCesamOwRappresentante =
      getMappingIdCesamOwRappresentante(thunkApi);

    if (isNecessaryProspectCall(thunkApi)) {
      const filter = {
        ...dispositionsDocumentFilter,
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect = await searchDocuments(
        thunkApi,
        filter,
        prospectToken
      );
      result = { ...result, ...resultProspect };
    }

    const bundles = filterDocumentsBundle(result, ['SIGNED', 'REJECTED']);

    const details = await getBundlesDetails(bundles, thunkApi);

    const parser = parseDispositionBundles(details);

    const resMap = new Map<string, DocumentBeanComplexMetadata>();
    const currentFilter = addFilter(thunkApi, {
      ...dispositionsDocumentFilter,
    });
    const resActual = await searchDocumentsByFilter(thunkApi, {
      ...addFilter(thunkApi, { ...dispositionsDocumentFilter }),
      customerUserRegisterCode: undefined,
    });
    mergeDocumentsIntoMap(resMap, resActual);

    await fetchAndMergeAccessoryDocuments(thunkApi, currentFilter, resMap);

    if (isNecessaryProspectCall(thunkApi)) {
      const filter = {
        ...addFilter(thunkApi, { ...dispositionsDocumentFilter }),
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect: Array<DocumentBeanComplexMetadata> =
        await searchDocumentsByFilter(thunkApi, filter, prospectToken);

      mergeDocumentsIntoMap(resMap, resultProspect);
    }

    const archiveDocuments = filterArchivedDocuments(
      Array.from(resMap.values()),
      ['SIGNED', 'REJECTED']
    );

    if (archiveDocuments?.length > 0) {
      const archivePractice: Practice<DispositionDocument> =
        parseDispositionArchived(archiveDocuments);
      parser.push(archivePractice);
    }
    return parser;
  };
  getExpiredDocumentsList = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ): Promise<Practice<ExpiredDocument>[]> => {
    const state = thunkApi.getState();
    const user = state.data?.user;
    const isPg = user.userType === PersonaTipoEnum.Pg;

    let result = await searchDocuments(thunkApi, {
      ...expiredDocumentsFilter,
    });

    const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
    const mappingIdCesamOwRappresentante =
      getMappingIdCesamOwRappresentante(thunkApi);

    if (isNecessaryProspectCall(thunkApi)) {
      const filter = {
        ...expiredDocumentsFilter,
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect = await searchDocuments(
        thunkApi,
        filter,
        prospectToken
      );
      result = { ...result, ...resultProspect };
    }

    const filteredResult: any = {};
    Object.entries(result).forEach(([key, value]: [string, any]) => {
      const documents = value[1];
      if (documents && Array.isArray(documents)) {
        const hasExcludedType = documents.some(
          (doc: DocumentBeanComplexMetadata) =>
            shouldSkipDocumentType(
              doc.documentType,
              expiredSkippedDocumentTypes
            )
        );
        if (!hasExcludedType) {
          filteredResult[key] = value;
        }
      } else {
        filteredResult[key] = value;
      }
    });

    const bundles = filterDocumentsBundle(filteredResult, ['EXPIRED']);

    const details = await getBundlesDetails(bundles, thunkApi);

    const parser = parseExpiredDocumentsBundles(details);

    const resMap = new Map<string, DocumentBeanComplexMetadata>();
    const currentFilter = addFilter(thunkApi, {
      ...expiredDocumentsFilter,
    });
    const resActual = await searchDocumentsByFilter(thunkApi, currentFilter);
    mergeDocumentsIntoMap(resMap, resActual, expiredSkippedDocumentTypes);

    await fetchAndMergeAccessoryDocuments(
      thunkApi,
      currentFilter,
      resMap,
      undefined,
      expiredSkippedDocumentTypes
    );

    if (isNecessaryProspectCall(thunkApi)) {
      const filter = {
        ...addFilter(thunkApi, { ...expiredDocumentsFilter }),
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect: Array<DocumentBeanComplexMetadata> =
        await searchDocumentsByFilter(thunkApi, filter, prospectToken);

      mergeDocumentsIntoMap(
        resMap,
        resultProspect,
        expiredSkippedDocumentTypes
      );
    }

    const archiveDocuments = filterArchivedDocuments(
      Array.from(resMap.values()),
      ['EXPIRED']
    );

    if (archiveDocuments.length > 0) {
      const archivePractice: Practice<ExpiredDocument> =
        parseExpiredDocumentArchived(archiveDocuments);
      parser.push(archivePractice);
    }

    return parser;
  };

  getUnreadDocumentsCount = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ): Promise<UnreadDocumentsCount> => {
    const resultsList = await Promise.allSettled(
      unreadCountFiltersList.map(async (filter, index) => {
        if (
          index !== UnreadCountItemIndex.Offers &&
          index !== UnreadCountItemIndex.Communications
        ) {
          return 0;
        }

        const filterWithCommons = {
          ...addFilter(thunkApi, { ...filter }),
          read: false,
          status: filter.status || [],
        };

        // Solo per Communications
        if (index === UnreadCountItemIndex.Communications) {
          const dateTo = new Date();
          const dateFrom = new Date();
          dateFrom.setFullYear(dateFrom.getFullYear() - 1);

          filterWithCommons.publicationDateFrom =
            formatDateFromForServer(dateFrom);
          filterWithCommons.publicationDateTo = formatDateToForServer(dateTo);
        }

        const count = await countDocumentsByFilter(thunkApi, filterWithCommons);

        if (index === UnreadCountItemIndex.Communications) {
          return count;
        }

        const shouldFetchAccessory = shouldFetchAccessoryDocuments(
          thunkApi,
          filterWithCommons
        );

        if (!shouldFetchAccessory) {
          return count;
        }

        const accessoryCount = await countDocumentsByFilter(
          thunkApi,
          getAccessoryDocumentsFilter(filterWithCommons)
        );

        return count + accessoryCount;
      })
    );

    const counts = await countDocumentsSection(this, thunkApi);
    let response: UnreadDocumentsCount = {
      totalCommunications: 0,
      totalOffers: 0,
      counts,
    };

    resultsList.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const count = result.value;
        response = updateUnreadCountWithAPIResponse(response, count, index);
      }
    });

    return response;
  };

  getOfferDocuments = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>
  ): Promise<OfferingDocumentGroup[]> => {
    const state = thunkApi.getState();
    const user = state.data?.user;
    const isPg = user.userType === PersonaTipoEnum.Pg;
    const resultMap = new Map<string, DocumentBeanComplexMetadata>();
    const filter = addOfferFilter(thunkApi);

    const resultActual: Array<DocumentBeanComplexMetadata> =
      await searchDocumentsByFilter(thunkApi, filter);
    mergeDocumentsIntoMap(resultMap, resultActual);

    await fetchAndMergeAccessoryDocuments(thunkApi, filter, resultMap);

    const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
    const mappingIdCesamOwRappresentante =
      getMappingIdCesamOwRappresentante(thunkApi);

    if (isNecessaryProspectCall(thunkApi)) {
      const f = {
        ...addOfferFilter(thunkApi),
        customerCode: mappingIdCesamOw.idProspect,
      };

      if (isPg) {
        f.customerUserBehalfOfCode = mappingIdCesamOwRappresentante.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect: Array<DocumentBeanComplexMetadata> =
        await searchDocumentsByFilter(thunkApi, f, prospectToken);

      mergeDocumentsIntoMap(resultMap, resultProspect);
    }

    const finalResult = Array.from(resultMap.values());

    return [
      {
        type: 'AllFunds',
        documents: [
          {
            id: '1289472',
            fileUrl: 'https://allfunds.com/en/',
            title: 'DOCUMENTAZIONE INFORMATIVA AFB',
            flagExternalLink: true,
            company: 'ALLFUNDSBANK',
            flagIsRead: true,
          },
        ],
      },
      {
        type: 'Azimut',
        documents: [
          {
            id: '123',
            fileUrl: 'https://www.azimut.it/prodotti/fondi-azimut',
            title: 'KIID AZIMUT',
            flagExternalLink: true,
            company: 'Azimut',
            flagIsRead: true,
            type: 'KIID',
          },
          ...(finalResult ?? []).map((item) => parseOfferingDocumentItem(item)),
        ],
      },
    ];
  };

  getCommunicationDocuments = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>,
    filter: CommunicationsFilter
  ): Promise<CommunicationsDocument[]> => {
    const { benefits, dividends, operations, other, rangeDate, status } =
      filter;

    const benefitsFilter = benefits?.length > 0 ? benefits : [];
    const dividendsFilter = dividends?.length > 0 ? dividends : [];
    const otherLettersFilter = other?.length > 0 ? other : [];
    const operationsFilter = operations?.length > 0 ? operations : [];
    if (
      (!benefits && !dividends && !other && !operations) ||
      (benefits?.length <= 0 &&
        dividends?.length <= 0 &&
        other?.length <= 0 &&
        operations?.length <= 0)
    ) {
      benefitsFilter.push(...communicationBenefitsTypes);
      dividendsFilter.push(...communicationDividendsTypes);
      operationsFilter.push(...communicationOperationsTypes);
      otherLettersFilter.push(...communicationOtherLettersTypes);
    }

    /**
     * If status contains both 'Read' and 'NotRead' or if is empty, there is no need to
     * set the flag, because all documents must be returned
     * Otherwise the flag must be set depending on which of the 2 values is selected
     */

    let readFilter: boolean;
    if (status?.length === 1) {
      readFilter = status.indexOf('Read') !== -1;
    }

    const state = thunkApi.getState();
    const user = state.data?.user;
    const isPg = user.userType === PersonaTipoEnum.Pg;
    const isPf = !isPg;
    const resultMap = new Map<string, DocumentBeanComplexMetadata>();

    const resultActual: Array<DocumentBeanComplexMetadata> =
      await searchDocumentsByFilter(thunkApi, {
        customerCode: user.userCode,
        customerUserBehalfOfCode: user.userBehalfOfCode,
        customerUserRegisterCode: user.fiduciaria
          ? user.codiceRubrica
          : undefined,
        documentTypes: [
          ...benefitsFilter,
          ...dividendsFilter,
          ...otherLettersFilter,
          ...operationsFilter,
        ],
        publicationDateFrom: formatDateFromForServer(rangeDate.from),
        publicationDateTo: formatDateToForServer(rangeDate.to),
        read: readFilter,
        fieldsWithNullValueFilter: isPf ? ['customerUserBehalfOfCode'] : [],
      });

    mergeDocumentsIntoMap(resultMap, resultActual);

    const mappingIdCesamOw = getMappingIdCesamOw(thunkApi);
    const mappingIdCesamOwRappresentante =
      getMappingIdCesamOwRappresentante(thunkApi);

    if (isNecessaryProspectCall(thunkApi)) {
      const filter: DocumentSearchFilterRequest = {
        customerCode: mappingIdCesamOw.idProspect,
        documentTypes: [
          ...benefitsFilter,
          ...dividendsFilter,
          ...otherLettersFilter,
          ...operationsFilter,
        ],
        publicationDateFrom: formatDateFromForServer(rangeDate.from),
        publicationDateTo: formatDateToForServer(rangeDate.to),
        read: readFilter,
        fieldsWithNullValueFilter: isPf ? ['customerUserBehalfOfCode'] : [],
      };

      if (isPg) {
        filter.customerCode = mappingIdCesamOwRappresentante.idProspect;
        filter.customerUserBehalfOfCode = mappingIdCesamOw.idProspect;
      }

      const prospectToken: string | undefined = user.prospectToken;
      const resultProspect: Array<DocumentBeanComplexMetadata> =
        await searchDocumentsByFilter(thunkApi, filter, prospectToken);

      mergeDocumentsIntoMap(resultMap, resultProspect);
    }

    return Array.from(resultMap.values()).map((item) =>
      parseCommunicationDocumentItem(item)
    );
  };

  downloadDocument = async (
    thunkApi: GetThunkAPI<AsyncThunkConfig<DownloadDocumentsError>>,
    documentId: string,
    token?: string,
    behalf?: boolean
  ): Promise<Blob> => {
    const bhalf =
      (behalf &&
        thunkApi.getState().data.user.userType === PersonaTipoEnum.Pg) ||
      thunkApi.getState().features.userRegistration.selectedOptionUserType ===
        PersonaTipoEnum.Pg;
    const response = await apiManager.documentContentApi.downloadDocument2(
      documentId,
      bhalf,
      undefined,
      {
        ...getAPIRequestHeadersWithToken(thunkApi, token),
        responseType: 'blob',
      }
    );

    if (!isAxiosResponseOk(response)) {
      return genericApiError();
    } else {
      return response.data as Blob;
    }
  };
}

// Utility function used by getUnreadDocumentsCount to Update the response object
const updateUnreadCountWithAPIResponse = (
  cumulativeCount: UnreadDocumentsCount,
  count: number,
  requestIndex: number
) => {
  const result = { ...cumulativeCount };
  if (requestIndex === UnreadCountItemIndex.Communications) {
    result.totalCommunications = count;
  } else if (requestIndex === UnreadCountItemIndex.Offers) {
    result.totalOffers = count;
  }

  return result;
};

export const papyrusDocumentsRepository = new PapyrusDocumentsRepository();
